import React, { useState } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface AdmissionStatusProps {
  domain: string;
}

interface Application {
  id: string;
  applicationId: string;
  studentName: string;
  fatherName: string;
  appliedClass: string;
  email: string;
  phone: string;
  status: string;
  appliedOn: string;
  reviewedOn?: string;
  remarks?: string;
}

const AdmissionStatus: React.FC<AdmissionStatusProps> = ({ domain }) => {
  const [searchType, setSearchType] = useState<'applicationId' | 'email' | 'phone'>('applicationId');
  const [searchValue, setSearchValue] = useState('');
  const [searching, setSearching] = useState(false);
  const [application, setApplication] = useState<Application | null>(null);
  const [notFound, setNotFound] = useState(false);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!searchValue.trim()) {
      alert('Please enter a search value');
      return;
    }

    setSearching(true);
    setNotFound(false);
    setApplication(null);

    try {
      const applicationsRef = collection(db, `school-data/${domain}/applications`);
      const q = query(applicationsRef, where(searchType, '==', searchValue.trim()));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        setNotFound(true);
      } else {
        const doc = snapshot.docs[0];
        setApplication({
          id: doc.id,
          ...doc.data(),
        } as Application);
      }
    } catch (error: any) {
      console.error('Error searching application:', error);
      alert(`Failed to search: ${error.message}`);
    } finally {
      setSearching(false);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-secondary';
      case 'under-review': return 'bg-info';
      case 'interview-scheduled': return 'bg-warning';
      case 'accepted': return 'bg-success';
      case 'rejected': return 'bg-danger';
      default: return 'bg-secondary';
    }
  };

  const getStatusMessage = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Your application has been received and is pending review.';
      case 'under-review':
        return 'Your application is currently under review by our admissions team.';
      case 'interview-scheduled':
        return 'Congratulations! An interview has been scheduled for your application.';
      case 'accepted':
        return 'Congratulations! Your application has been accepted. Please contact the admissions office for next steps.';
      case 'rejected':
        return 'We regret to inform you that your application has not been accepted at this time.';
      default:
        return '';
    }
  };

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-white py-3">
        <h5 className="mb-0">
          <i className="bi bi-search me-2 text-primary-custom"></i>
          Check Admission Status
        </h5>
      </div>

      <div className="card-body">
        <div className="alert alert-info">
          <i className="bi bi-info-circle me-2"></i>
          Enter your Application ID, Email, or Phone number to check your admission status.
        </div>

        <form onSubmit={handleSearch}>
          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <label className="form-label">Search By</label>
              <select
                className="form-select"
                value={searchType}
                onChange={(e) => setSearchType(e.target.value as any)}
              >
                <option value="applicationId">Application ID</option>
                <option value="email">Email</option>
                <option value="phone">Phone Number</option>
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">
                {searchType === 'applicationId' && 'Application ID'}
                {searchType === 'email' && 'Email Address'}
                {searchType === 'phone' && 'Phone Number'}
              </label>
              <input
                type={searchType === 'email' ? 'email' : 'text'}
                className="form-control"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder={
                  searchType === 'applicationId'
                    ? 'e.g., ADM20250001'
                    : searchType === 'email'
                    ? 'e.g., parent@example.com'
                    : 'e.g., 9876543210'
                }
                required
              />
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <button
                type="submit"
                className="btn btn-primary w-100"
                disabled={searching}
              >
                {searching ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Searching...
                  </>
                ) : (
                  <>
                    <i className="bi bi-search me-2"></i>
                    Search
                  </>
                )}
              </button>
            </div>
          </div>
        </form>

        {notFound && (
          <div className="alert alert-warning">
            <i className="bi bi-exclamation-triangle me-2"></i>
            No application found with the provided information. Please check and try again.
          </div>
        )}

        {application && (
          <div className="card border-primary mt-4">
            <div className="card-header bg-primary bg-opacity-10">
              <h6 className="mb-0">Application Details</h6>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-12">
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <div>
                      <h4 className="mb-0">{application.studentName}</h4>
                      <small className="text-muted">Application ID: {application.applicationId}</small>
                    </div>
                    <div className="ms-auto">
                      <span className={`badge ${getStatusBadgeClass(application.status)} fs-6`}>
                        {application.status.replace('-', ' ').toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="col-12">
                  <div className={`alert ${
                    application.status === 'accepted' ? 'alert-success' :
                    application.status === 'rejected' ? 'alert-danger' :
                    application.status === 'interview-scheduled' ? 'alert-warning' :
                    'alert-info'
                  }`}>
                    <strong>Status Update:</strong> {getStatusMessage(application.status)}
                  </div>
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-bold text-muted small">Father's Name</label>
                  <p className="mb-0">{application.fatherName}</p>
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-bold text-muted small">Applied Class</label>
                  <p className="mb-0">Class {application.appliedClass}</p>
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-bold text-muted small">Email</label>
                  <p className="mb-0">{application.email}</p>
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-bold text-muted small">Phone</label>
                  <p className="mb-0">{application.phone}</p>
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-bold text-muted small">Applied On</label>
                  <p className="mb-0">{new Date(application.appliedOn).toLocaleDateString()}</p>
                </div>

                {application.reviewedOn && (
                  <div className="col-md-6">
                    <label className="form-label fw-bold text-muted small">Last Updated</label>
                    <p className="mb-0">{new Date(application.reviewedOn).toLocaleDateString()}</p>
                  </div>
                )}

                {application.remarks && (
                  <div className="col-12">
                    <label className="form-label fw-bold text-muted small">Remarks</label>
                    <p className="mb-0">{application.remarks}</p>
                  </div>
                )}

                {application.status === 'accepted' && (
                  <div className="col-12">
                    <div className="alert alert-success mb-0">
                      <h6 className="alert-heading">Next Steps:</h6>
                      <ol className="mb-0">
                        <li>Please visit the school office with the required documents</li>
                        <li>Complete the admission formalities</li>
                        <li>Pay the admission fees</li>
                        <li>Collect your admission confirmation letter</li>
                      </ol>
                    </div>
                  </div>
                )}

                {application.status === 'interview-scheduled' && (
                  <div className="col-12">
                    <div className="alert alert-warning mb-0">
                      <h6 className="alert-heading">Interview Scheduled:</h6>
                      <p className="mb-0">
                        An interview has been scheduled for your application. Please check your email
                        or contact the admissions office for interview date and time details.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {!application && !notFound && !searching && (
          <div className="text-center py-5">
            <i className="bi bi-clipboard-check display-1 text-muted"></i>
            <p className="mt-3 text-muted">Enter your details above to check your application status</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdmissionStatus;
